import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { STAGES } from "@/lib/api";
import { AlertTriangle, CalendarClock, Layers, Tv2, Loader2, ArrowUpRight, Sparkles } from "lucide-react";

const STAGE_ACCENT = {
  Idea: "#66666E", Script: "#4182D9", Voiceover: "#B565D9", Editing: "#D9683D",
  Thumbnail: "#3DB9D9", Scheduled: "#D9A441", Published: "#2E8B57",
};

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [channels, setChannels] = useState([]);
  const [insight, setInsight] = useState(null); // null = loading, string = ready
  const navigate = useNavigate();

  useEffect(() => {
    Promise.all([api.get("/dashboard"), api.get("/channels")]).then(([d, c]) => {
      setData(d.data);
      setChannels(c.data);
    });
    api.get("/dashboard/insight")
      .then((r) => setInsight(r.data.insight))
      .catch(() => setInsight(""));
  }, []);

  if (!data) return <div className="flex justify-center py-24"><Loader2 className="animate-spin text-primary" size={28} /></div>;

  const maxCount = Math.max(1, ...Object.values(data.stage_counts));

  return (
    <div className="fade-up space-y-8">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="font-mono text-xs text-primary tracking-[0.25em] uppercase mb-2">Cross-channel overview</p>
          <h1 className="font-display font-extrabold text-4xl sm:text-5xl tracking-tight">Command Center</h1>
        </div>
        <button
          onClick={() => navigate("/board")}
          data-testid="go-to-board-button"
          className="flex items-center gap-2 border border-border rounded-md px-4 py-2.5 text-sm font-medium hover:bg-secondary transition-colors duration-200"
        >
          Open pipeline <ArrowUpRight size={16} />
        </button>
      </header>

      {/* AI insight banner */}
      {insight !== "" && (
        <div data-testid="ai-insight-banner" className="flex items-center gap-3 rounded-lg border border-primary/40 bg-primary/10 px-4 py-3.5">
          <div className="h-8 w-8 shrink-0 rounded-md bg-primary/20 flex items-center justify-center">
            <Sparkles size={16} className="text-primary" />
          </div>
          {insight === null ? (
            <span className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="animate-spin text-primary" size={14} /> Analyzing your pipeline…
            </span>
          ) : (
            <p className="text-sm font-medium text-foreground leading-snug" data-testid="ai-insight-text">{insight}</p>
          )}
          <span className="ml-auto shrink-0 font-mono text-[10px] uppercase tracking-[0.2em] text-primary hidden sm:block">AI insight</span>
        </div>
      )}

      {/* Stat bento */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat icon={Tv2} label="Active channels" value={data.active_channels} sub={`${data.total_channels} total`} testid="stat-channels" />
        <Stat icon={Layers} label="Videos in pipeline" value={data.total_cards} sub="across all stages" testid="stat-total" />
        <Stat icon={CalendarClock} label="Due this week" value={data.due_this_week.length} sub="upcoming deadlines" testid="stat-due" />
        <Stat icon={AlertTriangle} label="Stalled cards" value={data.stalled.length} sub=">3 days in stage" danger={data.stalled.length > 0} testid="stat-stalled" />
      </div>

      {/* Stage bottleneck chart */}
      <section className="bg-[#121214] border border-border rounded-lg p-6">
        <h2 className="font-display font-bold text-lg tracking-tight mb-1">Pipeline by stage</h2>
        <p className="text-sm text-muted-foreground mb-6">Card volume per stage across every channel — spot the bottleneck.</p>
        <div className="space-y-3">
          {STAGES.map((s) => {
            const count = data.stage_counts[s] || 0;
            return (
              <div key={s} className="flex items-center gap-4" data-testid={`stage-bar-${s.toLowerCase()}`}>
                <span className="w-24 shrink-0 text-sm font-medium text-muted-foreground">{s}</span>
                <div className="flex-1 h-7 bg-[#0A0A0B] rounded-md overflow-hidden border border-border">
                  <div
                    className="h-full rounded-md transition-all duration-500 flex items-center justify-end pr-2"
                    style={{ width: `${(count / maxCount) * 100}%`, background: STAGE_ACCENT[s], minWidth: count ? "28px" : "0" }}
                  >
                    {count > 0 && <span className="font-mono text-xs font-bold text-black/80">{count}</span>}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <div className="grid lg:grid-cols-2 gap-4">
        {/* Stalled */}
        <section className="bg-[#121214] border border-border rounded-lg p-6">
          <div className="flex items-center gap-2 mb-4">
            <AlertTriangle size={18} className="text-destructive" />
            <h2 className="font-display font-bold text-lg tracking-tight">Stalled — needs attention</h2>
          </div>
          {data.stalled.length === 0 ? (
            <Empty text="Nothing stalled. Everything is moving." />
          ) : (
            <ul className="space-y-2" data-testid="stalled-list">
              {data.stalled.map((c) => (
                <ItemRow key={c.id} card={c} onClick={() => navigate("/board")} stalled />
              ))}
            </ul>
          )}
        </section>

        {/* Due this week */}
        <section className="bg-[#121214] border border-border rounded-lg p-6">
          <div className="flex items-center gap-2 mb-4">
            <CalendarClock size={18} className="text-primary" />
            <h2 className="font-display font-bold text-lg tracking-tight">Due this week</h2>
          </div>
          {data.due_this_week.length === 0 ? (
            <Empty text="No deadlines in the next 7 days." />
          ) : (
            <ul className="space-y-2" data-testid="due-list">
              {data.due_this_week.map((c) => (
                <ItemRow key={c.id} card={c} onClick={() => navigate("/board")} />
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, sub, danger, testid }) {
  return (
    <div data-testid={testid} className={`bg-[#121214] border rounded-lg p-5 transition-colors duration-200 ${danger ? "border-destructive/40" : "border-border hover:border-primary/40"}`}>
      <Icon size={18} className={danger ? "text-destructive" : "text-primary"} />
      <p className="font-mono text-4xl font-bold mt-4 tracking-tight">{value}</p>
      <p className="text-sm font-medium mt-1">{label}</p>
      <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>
    </div>
  );
}

function ItemRow({ card, onClick, stalled }) {
  const due = card.due_date ? new Date(card.due_date).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : null;
  return (
    <li
      onClick={onClick}
      className="group flex items-center justify-between gap-3 p-3 rounded-md bg-[#1A1A1C] border border-border hover:border-primary/50 cursor-pointer transition-colors duration-200"
    >
      <div className="min-w-0">
        <p className="text-sm font-medium truncate group-hover:text-primary transition-colors duration-200">{card.title}</p>
        <p className="font-mono text-[11px] text-muted-foreground mt-0.5">{card.channel_name} · {card.stage}</p>
      </div>
      {stalled ? (
        <span className="shrink-0 font-mono text-[10px] font-bold uppercase px-2 py-1 rounded bg-destructive/20 text-destructive border border-destructive/50 animate-pulse">Stalled</span>
      ) : (
        <span className="shrink-0 font-mono text-[11px] text-primary">{due}</span>
      )}
    </li>
  );
}

function Empty({ text }) {
  return <p className="text-sm text-muted-foreground py-6 text-center">{text}</p>;
}
